import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AIAssistantModal from "../AIAssistantModal";
import AIButton from "../AIButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function ExerciseDialog({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  categories,
  muscleGroups,
  equipments,
  onSave,
}) {
  const [aiRun, setAiRun] = useState(null);
  const toggleSecondary = (m) => {
    const curr = form.secondary_muscle_groups || [];
    const next = curr.includes(m) ? curr.filter((x) => x !== m) : [...curr, m];
    setForm({ ...form, secondary_muscle_groups: next });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#171717] border-[#262626] text-white max-w-md" data-testid="exercise-library-dialog">
        <DialogHeader>
          <DialogTitle
            className="font-bold tracking-tight uppercase"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            {editing ? "MODIFICA ESERCIZIO" : "NUOVO ESERCIZIO"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-[#0A0A0A] border-[#262626] text-white"
              placeholder="es. Panca piana"
              data-testid="exercise-lib-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Categoria</Label>
              <Select
                value={form.category || "unset"}
                onValueChange={(v) => setForm({ ...form, category: v === "unset" ? "" : v })}
              >
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="exercise-lib-category">
                  <SelectValue placeholder="-" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  <SelectItem value="unset" className="text-white">-</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Attrezzatura</Label>
              <Select
                value={form.equipment || "unset"}
                onValueChange={(v) => setForm({ ...form, equipment: v === "unset" ? "" : v })}
              >
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="exercise-lib-equipment">
                  <SelectValue placeholder="-" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  <SelectItem value="unset" className="text-white">-</SelectItem>
                  {equipments.map((e) => (
                    <SelectItem key={e} value={e} className="text-white">{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Gruppo Muscolare Primario</Label>
              <Select
                value={form.muscle_group || "unset"}
                onValueChange={(v) => setForm({ ...form, muscle_group: v === "unset" ? "" : v })}
              >
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="exercise-lib-muscle">
                  <SelectValue placeholder="-" />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  <SelectItem value="unset" className="text-white">-</SelectItem>
                  {muscleGroups.map((m) => (
                    <SelectItem key={m} value={m} className="text-white">{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                Gruppi Muscolari Secondari
                {form.secondary_muscle_groups?.length > 0 && (
                  <span className="ml-1.5 text-[10px] text-[#34C759] normal-case tracking-normal">
                    · {form.secondary_muscle_groups.length} selezionati
                  </span>
                )}
              </Label>
              <div
                className="mt-1 bg-[#0A0A0A] border border-[#262626] rounded-md p-2 flex flex-wrap gap-1.5 min-h-[38px]"
                data-testid="exercise-lib-secondary-multi"
              >
                {muscleGroups
                  .filter((m) => m !== form.muscle_group)
                  .map((m) => {
                    const active = form.secondary_muscle_groups?.includes(m);
                    return (
                      <button
                        key={`sec-${m}`}
                        type="button"
                        onClick={() => toggleSecondary(m)}
                        className={`text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                          active
                            ? "bg-[#34C759]/15 border-[#34C759]/50 text-[#34C759]"
                            : "bg-[#171717] border-[#262626] text-zinc-500 hover:text-zinc-300 hover:border-[#3a3a3a]"
                        }`}
                        data-testid={`exercise-lib-secondary-${m}`}
                      >
                        {m}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrizione</Label>
              <AIButton
                label="Genera con AI"
                testId="exercise-ai-generate"
                disabled={!form.name?.trim()}
                onClick={() => setAiRun({
                  exercise_name: form.name,
                  muscle_group: form.muscle_group || null,
                  equipment: form.equipment || null,
                })}
              />
            </div>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 bg-[#0A0A0A] border-[#262626] text-white min-h-[60px] resize-none"
              placeholder="Note sull'esecuzione..."
              data-testid="exercise-lib-description"
            />
          </div>
        </div>
        {aiRun && (
          <AIAssistantModal
            endpoint="exercise-description"
            payload={aiRun}
            title={`Descrizione: ${aiRun.exercise_name}`}
            onClose={() => setAiRun(null)}
          />
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A]"
            data-testid="exercise-lib-cancel"
          >
            Annulla
          </Button>
          <Button
            onClick={onSave}
            className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
            data-testid="exercise-lib-save"
          >
            {editing ? "Aggiorna" : "Crea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
